import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaHome, FaChartLine, FaUser, FaClock, FaComments } from 'react-icons/fa';
import { FaArrowRightFromBracket, FaLocationDot } from 'react-icons/fa6';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { useStudentAuth } from '../../../lib/AuthProvider';
import { getSupabase } from '../../../lib/supabaseClient';
import './Progress.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Progress: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { student, loading: authLoading, signOut } = useStudentAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [totalHoursRendered, setTotalHoursRendered] = useState(0);
  const requiredHours = 500;

  // Fetch total hours from database
  useEffect(() => {
    const fetchTotalHours = async () => {
      if (!student?.id) return;

      try {
        const supabase = getSupabase();
        
        // Fetch daily_hours for this student
        const { data: dailyHours, error } = await supabase
          .from('daily_hours')
          .select('total_hours')
          .eq('user_id', student.id);

        if (error) {
          console.error('Error fetching daily hours:', error);
          return;
        }

        if (dailyHours && dailyHours.length > 0) {
          // Calculate total hours
          const total = (dailyHours as { total_hours: number }[]).reduce((sum, record) => sum + (record.total_hours || 0), 0);
          setTotalHoursRendered(Math.round(total * 100) / 100);
        }
      } catch (error) {
        console.error('Error fetching hours:', error);
      }
    };

    fetchTotalHours();
  }, [student?.id]);

  // Calculate progress percentage
  const progressPercentage = Math.min(Math.round((totalHoursRendered / requiredHours) * 100), 100);

  // Check session validity on component mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const supabase = getSupabase();
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          console.log('Session invalid in Progress, redirecting to login');
          navigate('/', { replace: true });
          return;
        }
        
        // If session exists but no student data and not loading, wait a bit more
        if (!student && !authLoading) {
          console.log('No student data but session exists, waiting for AuthProvider to load...');
          setTimeout(() => {
            if (!student && !authLoading) {
              console.log('Still no student data after timeout, redirecting to login');
              navigate('/', { replace: true });
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking session in Progress:', error);
      }
    };

    if (!authLoading) {
      checkSession();
    }
  }, [student, authLoading, navigate]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const dailyTimeRecords = [
    { day: 'Monday', hours: 7 },
    { day: 'Tuesday', hours: 3 },
    { day: 'Wednesday', hours: 5 },
    { day: 'Thursday', hours: 7 },
    { day: 'Friday', hours: 4 },
    { day: 'Saturday', hours: 6 }
  ];

  return (
    <div className="student-home">
      <header className="top-nav">
        <div className="logo">
          <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
        </div>
        <button
          className="hamburger-btn"
          aria-label="Open menu"
          aria-controls="mobile-sidebar"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(true)}
        >
          {'\u2630'}
        </button>
        <div className="nav-links">
          <button 
            onClick={() => navigate('/student/home')} 
            className={`nav-link ${isActive('/student/home') ? 'active' : ''}`}
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/student/attendance')} 
            className={`nav-link ${isActive('/student/attendance') ? 'active' : ''}`}
          >
            <FaClock className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/student/progress')} 
            className={`nav-link ${isActive('/student/progress') ? 'active' : ''}`}
          >
            <FaChartLine className="nav-icon" /> Progress
          </button>
          <button 
            onClick={() => navigate('/student/feedback')} 
            className={`nav-link ${isActive('/student/feedback') ? 'active' : ''}`}
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/student/profile')} 
            className={`nav-link ${isActive('/student/profile') ? 'active' : ''}`}
          >
            <FaUser className="nav-icon" /> Profile
          </button>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="mobile-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={() => setIsMenuOpen(false)}>
          <button 
            onClick={() => navigate('/student/home')} 
            className="nav-link"
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/student/attendance')} 
            className="nav-link"
          >
            <FaClock className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/student/progress')} 
            className="nav-link"
          >
            <FaChartLine className="nav-icon" /> Progress
          </button>
          <button 
            onClick={() => navigate('/student/feedback')} 
            className="nav-link"
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/student/profile')} 
            className="nav-link"
          >
            <FaUser className="nav-icon" /> Profile
          </button>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </nav>
      </aside>

      <main className="main-content">
        <div className="welcome-section">
          <h1>Progress</h1>
          <p>Your OJT progress overview</p>
        </div>

        {/* OJT Progress Summary */}
        <div className="ojt-progress-section">
          <div className="panel ojt-progress-panel">
            <div className="panel-header">OJT Progress</div>
            <div className="panel-content ojt-progress-content">
              <div className="progress-circle" style={{ background: `conic-gradient(#0044cc ${progressPercentage * 3.6}deg, #e0e0e0 0deg)` }}>
                <div className="progress-value">{progressPercentage}%</div>
                <div className="progress-label">Completed</div>
              </div>
              <div className="progress-stats">
                <div className="stat-item">
                  <span className="stat-value">{totalHoursRendered}</span>
                  <span className="stat-label">Hours Rendered</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{requiredHours}</span>
                  <span className="stat-label">Required Hours</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="progress-container">
          <div className="panel time-records-panel">
            <div className="panel-header">Daily Time Records</div>
            <div className="panel-content">
              <Bar
                data={{
                  labels: dailyTimeRecords.map(record => record.day),
                  datasets: [
                    {
                      label: 'Hours',
                      data: dailyTimeRecords.map(record => record.hours),
                      backgroundColor: '#0044cc',
                      borderRadius: 4,
                      maxBarThickness: 30
                    }
                  ]
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  scales: {
                    y: {
                      beginAtZero: true,
                      max: 8,
                      ticks: {
                        stepSize: 1,
                        callback: (value: number | string) => `${value} hours`
                      },
                      grid: {
                        color: '#e0e0e0'
                      }
                    },
                    x: {
                      grid: {
                        display: false
                      }
                    }
                  },
                  plugins: {
                    legend: {
                      display: false
                    },
                    tooltip: {
                      callbacks: {
                        label: (tooltipItem) => `${tooltipItem.raw} hours`
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Progress;

